import { useEffect, useState, type FormEvent } from 'react';
import { errorText, locationLabel, type Location } from '../api';
import { adminApi, type Brand, type EventType, type EventTypePatch } from './api';
import { Link, navigate } from './app';
import { RulesEditor } from './rules';

/** Ein Auftritt: Erscheinungsbild, rechtliche Links und seine Terminarten. */
export function BrandEditPage({ id }: { id: string }) {
  const [brand, setBrand] = useState<Brand | null | undefined>(undefined);

  useEffect(() => {
    adminApi
      .getBrand(id)
      .then(setBrand)
      .catch(() => setBrand(null));
  }, [id]);

  if (brand === undefined) return <p className="muted">Lade …</p>;
  if (brand === null) {
    return (
      <>
        <h1>Auftritt nicht gefunden</h1>
        <p>
          <Link href="/admin">Zurück zur Übersicht</Link>
        </p>
      </>
    );
  }

  return (
    <>
      <p className="small">
        <Link href="/admin">← Alle Auftritte</Link>
      </p>
      <div className="admin-head">
        <div>
          <h1>{brand.name}</h1>
          <p className="muted">
            Buchungsseite: <a href={`/${brand.slug}`}>/{brand.slug}</a>
          </p>
        </div>
      </div>
      <div className="admin-grid">
        <div className="stack">
          <BrandSettings brand={brand} onChange={setBrand} />
          <LogoCard brand={brand} onChange={setBrand} />
          <LegalCard brand={brand} onChange={setBrand} />
          <DangerCard brand={brand} />
        </div>
        <div className="stack">
          <EventTypesCard brand={brand} onChange={setBrand} />
        </div>
      </div>
    </>
  );
}

function useSave<T>(save: (value: T) => Promise<Brand>, onChange: (b: Brand) => void) {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  async function run(value: T) {
    setBusy(true);
    setMessage(null);
    try {
      onChange(await save(value));
      setMessage({ kind: 'ok', text: 'Gespeichert.' });
    } catch (e) {
      setMessage({ kind: 'error', text: errorText(e) });
    } finally {
      setBusy(false);
    }
  }
  return { busy, message, run };
}

function Feedback({ message }: { message: { kind: 'ok' | 'error'; text: string } | null }) {
  if (!message) return null;
  return <span className={message.kind === 'ok' ? 'success-inline' : 'error-inline'}>{message.text}</span>;
}

function BrandSettings({ brand, onChange }: { brand: Brand; onChange: (b: Brand) => void }) {
  const [name, setName] = useState(brand.name);
  const [slug, setSlug] = useState(brand.slug);
  const [senderName, setSenderName] = useState(brand.senderName ?? '');
  const [color, setColor] = useState(brand.brandColor ?? '#1f4e5f');
  const [useColor, setUseColor] = useState(brand.brandColor !== null);
  const { busy, message, run } = useSave(() => adminApi.updateBrand(brand.id, { name, slug, senderName: senderName || null, brandColor: useColor ? color : null }), onChange);

  return (
    <form
      className="card"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        void run(undefined);
      }}
    >
      <h2 className="card__title">Auftritt</h2>
      <div className="field">
        <label htmlFor="brand-name">Name</label>
        <input id="brand-name" type="text" required value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor="brand-slug">Adresse der Buchungsseite</label>
        <div className="input-prefix">
          <span>{window.location.origin}/</span>
          <input id="brand-slug" type="text" required value={slug} onChange={(e) => setSlug(e.target.value)} />
        </div>
        <span className="muted small">Kleinbuchstaben, Ziffern und Bindestriche. Ändern macht alte Links ungültig.</span>
      </div>
      <div className="field">
        <label htmlFor="brand-sender">Absendername für Mails</label>
        <input id="brand-sender" type="text" placeholder={brand.name} value={senderName} onChange={(e) => setSenderName(e.target.value)} />
        <span className="muted small">Leer = der Name des Auftritts.</span>
      </div>
      <div className="field">
        <label>Farbe</label>
        <label className="choice">
          <input type="checkbox" checked={useColor} onChange={(e) => setUseColor(e.target.checked)} />
          <span>Eigene Markenfarbe</span>
          {useColor && <input type="color" value={color} onChange={(e) => setColor(e.target.value)} />}
        </label>
      </div>
      <div className="save-bar">
        <button type="submit" className="button" disabled={busy}>
          Speichern
        </button>
        <Feedback message={message} />
      </div>
    </form>
  );
}

function LogoCard({ brand, onChange }: { brand: Brand; onChange: (b: Brand) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function upload(file: File | undefined) {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const { logoUrl } = await adminApi.uploadLogo(brand.id, file);
      onChange({ ...brand, logoUrl });
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    try {
      await adminApi.removeLogo(brand.id);
      onChange({ ...brand, logoUrl: null });
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <h2 className="card__title">Logo</h2>
      {brand.logoUrl ? <img className="logo-preview" src={brand.logoUrl} alt={brand.name} /> : <p className="muted small">Ohne Logo steht oben auf der Buchungsseite der Name.</p>}
      <div className="save-bar">
        <label className="button button--secondary button--small">
          {busy ? 'Einen Moment …' : brand.logoUrl ? 'Anderes Logo' : 'Logo hochladen'}
          <input type="file" accept="image/png,image/jpeg,image/webp" hidden disabled={busy} onChange={(e) => void upload(e.target.files?.[0])} />
        </label>
        {brand.logoUrl && (
          <button type="button" className="link-button" disabled={busy} onClick={() => void remove()}>
            Entfernen
          </button>
        )}
        {error && <span className="error-inline">{error}</span>}
      </div>
      <p className="muted small">JPG, PNG oder WebP, bis 2 MB. Am besten mit durchsichtigem Hintergrund.</p>
    </div>
  );
}

function LegalCard({ brand, onChange }: { brand: Brand; onChange: (b: Brand) => void }) {
  const [imprint, setImprint] = useState(brand.imprintUrl ?? '');
  const [privacy, setPrivacy] = useState(brand.privacyUrl ?? '');
  const { busy, message, run } = useSave(() => adminApi.updateBrand(brand.id, { imprintUrl: imprint || null, privacyUrl: privacy || null }), onChange);

  return (
    <form
      className="card"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        void run(undefined);
      }}
    >
      <h2 className="card__title">Impressum und Datenschutz</h2>
      <p className="muted small">Die Buchungsseite verlinkt beides im Fuß – deine Kunden tragen dort Name und E-Mail ein.</p>
      <div className="field">
        <label htmlFor="imprint">Link zum Impressum</label>
        <input id="imprint" type="url" placeholder="https://…" value={imprint} onChange={(e) => setImprint(e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor="privacy">Link zur Datenschutzerklärung</label>
        <input id="privacy" type="url" placeholder="https://…" value={privacy} onChange={(e) => setPrivacy(e.target.value)} />
      </div>
      <div className="save-bar">
        <button type="submit" className="button" disabled={busy}>
          Speichern
        </button>
        <Feedback message={message} />
      </div>
    </form>
  );
}

function DangerCard({ brand }: { brand: Brand }) {
  const [busy, setBusy] = useState(false);
  async function remove() {
    if (!window.confirm(`„${brand.name}" mit allen Terminarten löschen? Das lässt sich nicht rückgängig machen.`)) return;
    setBusy(true);
    try {
      await adminApi.deleteBrand(brand.id);
      navigate('/admin');
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="card">
      <h2 className="card__title">Auftritt löschen</h2>
      <p className="muted small">Entfernt den Auftritt samt Terminarten. Die Buchungsseite ist danach weg.</p>
      <div>
        <button type="button" className="button button--danger button--small" disabled={busy} onClick={() => void remove()}>
          Löschen
        </button>
      </div>
    </div>
  );
}

function EventTypesCard({ brand, onChange }: { brand: Brand; onChange: (b: Brand) => void }) {
  const [title, setTitle] = useState('');
  const [duration, setDuration] = useState(30);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  async function create(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { id } = await adminApi.createEventType(brand.id, title.trim(), duration);
      onChange(await adminApi.getBrand(brand.id));
      setTitle('');
      setOpen(id);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  function replace(updated: EventType) {
    onChange({ ...brand, eventTypes: brand.eventTypes.map((t) => (t.id === updated.id ? updated : t)) });
  }

  function drop(id: string) {
    onChange({ ...brand, eventTypes: brand.eventTypes.filter((t) => t.id !== id) });
    if (open === id) setOpen(null);
  }

  return (
    <div className="card">
      <h2 className="card__title">Terminarten</h2>
      <p className="muted small">Was gebucht werden kann, zum Beispiel „PraxiPet-Vorführung, 45 Minuten".</p>
      <form className="admin-create admin-create--inline" onSubmit={create}>
        <div className="field" style={{ flexGrow: 1 }}>
          <label htmlFor="new-type">Neue Terminart</label>
          <input id="new-type" type="text" placeholder="Titel" value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>
        <div className="field" style={{ width: 110 }}>
          <label htmlFor="new-duration">Minuten</label>
          <input id="new-duration" type="number" min={5} max={480} step={5} value={duration} onChange={(e) => setDuration(Number(e.target.value))} />
        </div>
        <button type="submit" className="button" disabled={busy || !title.trim()}>
          Anlegen
        </button>
        {error && <div className="error-inline">{error}</div>}
      </form>
      {brand.eventTypes.length === 0 && <p className="muted small">Noch keine Terminart.</p>}
      <div className="stack">
        {brand.eventTypes.map((t) => (
          <EventTypeRow key={t.id} brand={brand} eventType={t} open={open === t.id} onToggle={() => setOpen(open === t.id ? null : t.id)} onChange={replace} onDelete={() => drop(t.id)} />
        ))}
      </div>
    </div>
  );
}

function EventTypeRow({ brand, eventType, open, onToggle, onChange, onDelete }: { brand: Brand; eventType: EventType; open: boolean; onToggle: () => void; onChange: (t: EventType) => void; onDelete: () => void }) {
  const [form, setForm] = useState<EventTypePatch & { description: string }>({
    title: eventType.title,
    slug: eventType.slug,
    description: eventType.description ?? '',
    durationMinutes: eventType.durationMinutes,
    location: eventType.location,
    active: eventType.active,
    leadTimeHours: eventType.leadTimeHours,
    horizonDays: eventType.horizonDays,
    requiresEmailVerification: eventType.requiresEmailVerification,
    requiresHostConfirmation: eventType.requiresHostConfirmation,
  });
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const field = <K extends keyof typeof form>(key: K) => (value: (typeof form)[K]) => setForm((f) => ({ ...f, [key]: value }));

  async function save(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setMessage(null);
    try {
      onChange(await adminApi.updateEventType(eventType.id, { ...form, description: form.description || null }));
      setMessage({ kind: 'ok', text: 'Gespeichert.' });
    } catch (e) {
      setMessage({ kind: 'error', text: errorText(e) });
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!window.confirm(`„${eventType.title}" löschen?`)) return;
    setBusy(true);
    try {
      await adminApi.deleteEventType(eventType.id);
      onDelete();
    } catch (e) {
      setMessage({ kind: 'error', text: errorText(e) });
      setBusy(false);
    }
  }

  return (
    <div className={`event-type${open ? ' is-open' : ''}`}>
      <button type="button" className="event-type__head" onClick={onToggle}>
        <span>
          <strong className={eventType.active ? '' : 'muted'}>{eventType.title}</strong>
          <span className="faint small">
            {' '}
            · {eventType.durationMinutes} Min. · {locationLabel(eventType.location)}
            {eventType.active ? '' : ' · nicht buchbar'}
          </span>
        </span>
        <span className="faint">{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <form className="event-type__body" onSubmit={save}>
          <div className="field">
            <label htmlFor={`title-${eventType.id}`}>Titel</label>
            <input id={`title-${eventType.id}`} type="text" required value={form.title} onChange={(e) => field('title')(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor={`slug-${eventType.id}`}>Adresse</label>
            <div className="input-prefix">
              <span>/{brand.slug}/</span>
              <input id={`slug-${eventType.id}`} type="text" required value={form.slug} onChange={(e) => field('slug')(e.target.value)} />
            </div>
          </div>
          <div className="field">
            <label htmlFor={`desc-${eventType.id}`}>Kurze Beschreibung</label>
            <textarea id={`desc-${eventType.id}`} value={form.description} onChange={(e) => field('description')(e.target.value)} placeholder="Was erwartet deine Kundin in diesem Gespräch?" />
          </div>
          <div className="grid-2">
            <div className="field">
              <label htmlFor={`dur-${eventType.id}`}>Dauer in Minuten</label>
              <input id={`dur-${eventType.id}`} type="number" min={5} max={480} step={5} required value={form.durationMinutes} onChange={(e) => field('durationMinutes')(Number(e.target.value))} />
            </div>
            <div className="field">
              <label htmlFor={`loc-${eventType.id}`}>Ort</label>
              <select id={`loc-${eventType.id}`} value={form.location} onChange={(e) => field('location')(e.target.value as Location)}>
                <option value="meet">Google Meet</option>
                <option value="phone">Telefon</option>
              </select>
            </div>
            <div className="field">
              <label htmlFor={`lead-${eventType.id}`}>Frühestens in … Stunden</label>
              <input id={`lead-${eventType.id}`} type="number" min={0} max={720} required value={form.leadTimeHours} onChange={(e) => field('leadTimeHours')(Number(e.target.value))} />
              <span className="muted small">So kurzfristig darf gebucht werden.</span>
            </div>
            <div className="field">
              <label htmlFor={`hor-${eventType.id}`}>Höchstens … Tage im Voraus</label>
              <input id={`hor-${eventType.id}`} type="number" min={1} max={365} required value={form.horizonDays} onChange={(e) => field('horizonDays')(Number(e.target.value))} />
            </div>
          </div>
          <label className="choice">
            <input type="checkbox" checked={form.active} onChange={(e) => field('active')(e.target.checked)} />
            <span>Buchbar – erscheint auf der Buchungsseite</span>
          </label>
          <div className="field">
            <label>Schutz vor Spam</label>
            <label className="choice choice--stacked">
              <input type="checkbox" checked={form.requiresEmailVerification} onChange={(e) => field('requiresEmailVerification')(e.target.checked)} />
              <span>
                <strong>E-Mail-Code vor der Buchung (empfohlen)</strong>
                <span className="muted small"> – der Kunde bestätigt seine Adresse mit einem 6-stelligen Code; der Termin ist dabei zehn Minuten reserviert.</span>
              </span>
            </label>
            <label className="choice choice--stacked">
              <input type="checkbox" checked={form.requiresHostConfirmation} onChange={(e) => field('requiresHostConfirmation')(e.target.checked)} />
              <span>
                <strong>Bestätigung durch dich</strong>
                <span className="muted small"> – Buchungen sind erst Anfragen, die du unter „Buchungen“ annimmst oder ablehnst. Bis dahin ist die Zeit reserviert; Google-Termin und Bestätigungsmail kommen erst mit deinem Ja.</span>
              </span>
            </label>
          </div>
          <div className="save-bar">
            <button type="submit" className="button button--small" disabled={busy}>
              Speichern
            </button>
            <button type="button" className="link-button" disabled={busy} onClick={() => void remove()}>
              Löschen
            </button>
            <Feedback message={message} />
          </div>
          <hr className="rule" />
          <RulesEditor eventTypeId={eventType.id} />
        </form>
      )}
    </div>
  );
}
